import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useT } from '../core/lexicon';
import { vetoCount } from '../core/ballot';
import { seq } from '../core/util';
import { useVoting } from './useVoting';
import { ListenLink, VotingClosed, VotingLoading, votingRight } from './ballotShared';
import { Screen, TopBar } from './Layout';

/**
 * The veto: one pick, costing nothing from the budget. It only lands if at least
 * `veto_threshold` of the round pick the same track (threshold comes off the
 * round, never a constant). You cannot veto a track you backed (rule 5) or a
 * duplicate, which has already taken its penalty (rule 5b). Own track excluded.
 */
export function Veto() {
  const t = useT();
  const nav = useNavigate();
  const v = useVoting();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const title = t('phase.veto.name');
  if (v.loading) return <VotingLoading title={title} />;
  if (!v.round || v.round.phase !== 'VOTING') return <VotingClosed t={t} title={title} round={v.round} />;
  // Veto disabled for this season → the screen shouldn't exist; send them home.
  if (v.round.veto_enabled === false) return <Navigate to="/" replace />;

  const { draft, eco, round } = v;
  const threshold = round.veto_threshold;
  const roster = round.expected_players;
  const picked = [...draft.entries()].find(([, d]) => d.is_veto)?.[0] ?? null;

  async function finish(run: () => Promise<{ submitted: boolean; error?: string }>) {
    setBusy(true);
    setProblem(null);
    setNote(null);
    const res = await run();
    if (res.error) {
      setProblem(res.error);
      setBusy(false);
    } else if (res.submitted) {
      nav('/');
    } else {
      setNote(t('ballot.draft'));
      setBusy(false);
    }
  }
  const send = () => finish(v.save);
  const abstain = () => finish(v.saveAbstain);

  return (
    <Screen>
      <TopBar title={t('round.label', { n: round.sequence_no })} sub={title} right={votingRight(round)} />
      <ListenLink t={t} round={round} />

      <section className="brief">
        <h2>{t('phase.veto.prompt').toUpperCase()}</h2>
        <p>{t('phase.veto.rule', { n: eco.veto_penalty })}</p>
        <div className="gauge" role="img" aria-label={t('phase.veto.threshold', { a: threshold, b: roster })}>
          {Array.from({ length: roster }, (_, i) => (
            <i key={i} className={i < threshold ? 'need' : undefined} />
          ))}
          <span>{t('phase.veto.threshold', { a: threshold, b: roster }).toUpperCase()}</span>
        </div>
      </section>

      <div className="list">
        {v.votable.map((tr) => {
          const dim = draft.get(tr.submission_id);
          const backed = (dim?.points ?? 0) > 0;
          const blocked = backed || tr.is_duplicate;
          const on = picked === tr.submission_id;
          return (
            <button
              key={tr.submission_id}
              className={`tr${on ? ' on' : ''}${blocked ? ' locked' : ''}`}
              disabled={blocked}
              onClick={() => v.setVeto(tr.submission_id)}
            >
              <span className="n">{seq(tr.display_order)}</span>
              <span>
                <span className="t2">{tr.title}</span>
                <span className="a">
                  {tr.subtitle ?? ''}
                  {tr.is_duplicate ? ` · ${t('duplicate.flag').toUpperCase()}` : backed ? ' · BACKED' : ''}
                </span>
              </span>
              <span className={`mk${on ? '' : ' off'}`}>{on ? '✗' : ' '}</span>
            </button>
          );
        })}
      </div>

      <div className="act">
        {problem ? <div className="errline" style={{ marginBottom: 8 }}>{problem}</div> : null}
        {note ? <div className="errline" style={{ marginBottom: 8, background: 'transparent', color: 'var(--k1)' }}>{note}</div> : null}
        <button className="go" disabled={vetoCount(draft) !== 1 || busy} onClick={send}>
          {busy ? '…' : t('phase.veto.cta').toUpperCase()}
        </button>
        <button
          className="go"
          disabled={busy}
          onClick={abstain}
          style={{ marginTop: 8, background: 'transparent', color: 'var(--k0)', border: '2px solid var(--k0)' }}
        >
          {t('phase.veto.abstain').toUpperCase()}
        </button>
      </div>
    </Screen>
  );
}
